import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { useRef, useState } from 'react';

import { Alert } from '@design-system/components/alert/alert';
import { Modal } from '@design-system/components/modal/modal';
import { Switch } from '@design-system/components/switch/switch';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { Typography } from '@design-system/components/typography/typography';

import {
  AutomationRow,
  AutomationSection,
  AutomationsViewModel,
  useAutomationsViewModel,
} from '../view-model/Automations.viewModel';
import * as styles from './Automations.view.css';
import { renderAutomationConfigForm } from './components/AutomationConfigForms';
import { AutomationDetailsModal } from './components/AutomationDetailsModal.view';
import { AutomationInsightsDrawer } from './components/AutomationInsightsDrawer.view';
import { AutomationLogsView } from './components/AutomationLogs.view';

type AutomationsViewProps = { creatorId: number };

export const AutomationsView = ({ creatorId }: AutomationsViewProps) => {
  const vm = useAutomationsViewModel(creatorId);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [detailsType, setDetailsType] = useState<string | null>(null);
  const onOpenDetails = (automationType: string) =>
    setDetailsType(automationType);

  if (vm.type === 'not-fetched' || vm.type === 'fetching') {
    return (
      <div
        className={styles.skeleton}
        aria-busy
        data-testid="automations-loading"
      />
    );
  }
  if (vm.type === 'fetch-error') {
    return (
      <Alert
        variant="error"
        details={vm.value.message}
        action={{ label: 'Try again', onClick: () => void vm.value.retry() }}
      />
    );
  }

  return (
    <div className={styles.list}>
      <HeaderCard onViewInsights={() => setInsightsOpen(true)} />
      <AutomationInsightsDrawer
        creatorId={creatorId}
        isOpen={insightsOpen}
        onClose={() => setInsightsOpen(false)}
        onSetUp={(automationType) => {
          setInsightsOpen(false);
          onOpenDetails(automationType);
        }}
      />
      <AutomationDetailsModal
        creatorId={creatorId}
        automationType={detailsType}
        onClose={() => setDetailsType(null)}
        renderConfigForm={(details) =>
          renderAutomationConfigForm(creatorId, details)
        }
      />
      {vm.value.needsSetup ? (
        <SetupBanner
          count={vm.value.needsSetup.count}
          onSetUp={() =>
            onOpenDetails(vm.value.needsSetup?.firstAutomationType ?? '')
          }
        />
      ) : null}
      <AutomationList vm={vm.value} onOpenDetails={onOpenDetails} />
      {vm.value.adminTools ? <AdminTools creatorId={creatorId} /> : null}
    </div>
  );
};

const HeaderCard = ({ onViewInsights }: { onViewInsights: () => void }) => (
  <section className={styles.headerCard} aria-labelledby="automations-title">
    <Typography
      as="h2"
      variant="body"
      size="large"
      fontWeight="medium"
      id="automations-title"
    >
      Automations
    </Typography>
    <Typography variant="body" size="medium" className={styles.headerIntro}>
      Well-timed messages that help you win members and keep them longer,
      personalized with your name, branding and program details. Nothing sends
      until you turn it on.
    </Typography>
    <div>
      <button
        type="button"
        className={styles.viewInsights}
        onClick={onViewInsights}
      >
        View insights
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  </section>
);

const setupBannerCopy = (count: number) => ({
  lead: `${count} ${
    count === 1 ? 'automation needs' : 'automations need'
  } a detail from you.`,
  rest: `We cannot send ${
    count === 1 ? 'it' : 'them'
  } until you fill it in. Takes about a minute.`,
  cta: `Set up ${count} ${count === 1 ? 'automation' : 'automations'}`,
});

const SetupBanner = ({
  count,
  onSetUp,
}: {
  count: number;
  onSetUp: () => void;
}) => {
  const copy = setupBannerCopy(count);
  return (
    <div className={styles.banner} role="status">
      <FontAwesomeIcon
        icon={faTriangleExclamation}
        className={styles.bannerIcon}
      />
      <p className={styles.bannerText}>
        <span className={styles.bannerLead}>{copy.lead}</span> {copy.rest}
      </p>
      <button type="button" className={styles.bannerCta} onClick={onSetUp}>
        {copy.cta}
      </button>
    </div>
  );
};

const AutomationList = ({
  vm,
  onOpenDetails,
}: {
  vm: AutomationsViewModel;
  onOpenDetails?: (automationType: string) => void;
}) => {
  const [confirming, setConfirming] = useState<string | null>(null);
  const switchRefs = useRef<Record<string, HTMLElement | null>>({});
  const rows = vm.sections.flatMap((section) => section.rows);
  const rowToDisable = rows.find((row) => row.automationType === confirming);

  const closeModal = () => {
    const type = confirming;
    setConfirming(null);
    if (type) requestAnimationFrame(() => switchRefs.current[type]?.focus());
  };

  return (
    <>
      {vm.sections.map((section) => (
        <SectionView
          key={section.key}
          section={section}
          onToggle={(row, next) =>
            next
              ? void vm.update(row.automationType, true)
              : setConfirming(row.automationType)
          }
          onOpenDetails={onOpenDetails}
          registerSwitch={(type, el) => {
            switchRefs.current[type] = el;
          }}
        />
      ))}
      <Modal
        isOpen={confirming !== null}
        onClose={closeModal}
        disableBackdropClick={rowToDisable?.pending}
        title={rowToDisable?.disableConfirmation.title ?? ''}
        description={rowToDisable?.disableConfirmation.body ?? ''}
        secondaryCta={{
          caption: 'Cancel',
          onClick: closeModal,
          state: rowToDisable?.pending ? 'disabled' : undefined,
        }}
        primaryCta={{
          caption: 'Turn off automation',
          state: !rowToDisable || rowToDisable.pending ? 'disabled' : undefined,
          onClick: async () => {
            if (!rowToDisable) return;
            await vm.update(rowToDisable.automationType, false);
            closeModal();
          },
        }}
      />
    </>
  );
};

type SectionViewProps = {
  section: AutomationSection;
  onToggle: (row: AutomationRow, next: boolean) => void;
  onOpenDetails?: (automationType: string) => void;
  registerSwitch: (automationType: string, el: HTMLElement | null) => void;
};

const SectionView = ({
  section,
  onToggle,
  onOpenDetails,
  registerSwitch,
}: SectionViewProps) => (
  <section
    className={styles.section}
    aria-labelledby={`section-${section.key}`}
  >
    <div className={styles.sectionHeader}>
      <Typography
        as="h3"
        variant="body"
        size="large"
        fontWeight="medium"
        id={`section-${section.key}`}
      >
        {section.title}
      </Typography>
      {section.subtitle ? (
        <Typography
          variant="body"
          size="medium"
          className={styles.sectionSubtitle}
        >
          {section.subtitle}
        </Typography>
      ) : null}
    </div>
    <div className={styles.sectionCard}>
      {section.rows.map((row) => (
        <AutomationRowView
          key={row.automationType}
          row={row}
          onToggle={(next) => onToggle(row, next)}
          onOpenDetails={
            onOpenDetails ? () => onOpenDetails(row.automationType) : undefined
          }
          registerSwitch={(el) => registerSwitch(row.automationType, el)}
        />
      ))}
    </div>
  </section>
);

type AutomationRowViewProps = {
  row: AutomationRow;
  onToggle: (next: boolean) => void;
  onOpenDetails?: () => void;
  registerSwitch: (el: HTMLElement | null) => void;
};

const AutomationRowView = ({
  row,
  onToggle,
  onOpenDetails,
  registerSwitch,
}: AutomationRowViewProps) => {
  const switchDisabled = row.pending || (!row.enabled && row.needsSetup);
  const control = (
    <div ref={registerSwitch} tabIndex={-1}>
      <Switch
        checked={row.enabled}
        onChange={onToggle}
        disabled={switchDisabled}
        showLabel
        ariaLabel={`${row.displayName}: ${row.enabled ? 'on' : 'off'}`}
        testId={`switch-${row.automationType}`}
      />
    </div>
  );

  return (
    <div
      className={
        onOpenDetails ? styles.automationRowClickable : styles.automationRow
      }
      data-testid={`automation-row-${row.automationType}`}
      onClick={onOpenDetails}
    >
      <div className={styles.iconTile} aria-hidden>
        <FontAwesomeIcon icon={row.icon} />
      </div>
      <div className={styles.rowBody}>
        <div className={styles.rowTitleLine}>
          <span className={styles.rowTitle}>{row.displayName}</span>
          {row.needsSetup ? (
            <span className={styles.badgeNeedsSetup}>
              <span className={styles.badgeDot} aria-hidden />
              Needs setup
            </span>
          ) : null}
          {row.sendsPush ? (
            <span className={styles.badgeChannels}>Email + push</span>
          ) : null}
        </div>
        <span className={styles.rowDescription}>{row.description}</span>
        <div className={styles.rowMeta}>
          <span>{row.cadence}</span>
          <span className={styles.metaDot} aria-hidden />
          <span>{row.triggerLine}</span>
          {row.conversionLine ? (
            <>
              <span className={styles.metaDot} aria-hidden />
              <span className={styles.conversionLine}>
                {row.conversionLine}
              </span>
            </>
          ) : null}
        </div>
        {row.error ? (
          <div className={styles.rowError} role="alert">
            {row.error}
            {row.retry ? (
              <>
                {' '}
                <button
                  type="button"
                  className={styles.viewInsights}
                  onClick={(event) => {
                    event.stopPropagation();
                    void row.retry?.();
                  }}
                >
                  Try again
                </button>
              </>
            ) : null}
          </div>
        ) : null}
        <span className={styles.status} aria-live="polite">
          {row.status ?? ''}
        </span>
      </div>
      <div
        className={styles.rowActions}
        onClick={(event) => event.stopPropagation()}
      >
        {row.setupHint && !row.enabled ? (
          <Tooltip variant="dark" title={row.setupHint} placement="top">
            {control}
          </Tooltip>
        ) : (
          control
        )}
        <button
          type="button"
          className={styles.chevronButton}
          aria-label={`Open ${row.displayName}`}
          onClick={onOpenDetails}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    </div>
  );
};

/** Pilot tooling from GRW-537, admin and moderator only. Send test lives in
 *  the details modal, per-type config in its form slot. */
const AdminTools = ({ creatorId }: { creatorId: number }) => (
  <div className={styles.adminTools} data-testid="admin-tools">
    <AutomationLogsView creatorId={creatorId} />
  </div>
);
